import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

export type InboxEvent =
  | { type: 'conversation.updated'; conversationId: string }
  | { type: 'message.created'; conversationId: string; channel: 'whatsapp' | 'instagram' }
  | { type: 'conversation.note'; conversationId: string };

const BASE = import.meta.env.VITE_PUBLIC_API_URL ?? '';

/**
 * Subscribes to the organization's real-time inbox events. Events carry ids only; the page
 * refetches through the API. Returns whether the socket is currently live, so the page can fall
 * back to polling when it is not (blocked WebSockets, API restart, ...).
 */
export function useInboxEvents(onEvent: (event: InboxEvent) => void, tenantId: string | undefined) {
  const [live, setLive] = useState(false);
  const handler = useRef(onEvent);
  useEffect(() => {
    handler.current = onEvent;
  });

  useEffect(() => {
    if (!tenantId) return;
    const socket = io(BASE || undefined, {
      path: '/socket.io',
      withCredentials: true,
      transports: ['websocket'],
      reconnectionDelayMax: 15_000,
    });
    socket.on('connect', () => setLive(true));
    socket.on('disconnect', () => setLive(false));
    socket.on('connect_error', () => setLive(false));
    socket.on('inbox', (event: InboxEvent) => handler.current(event));
    return () => {
      socket.close();
      setLive(false);
    };
  }, [tenantId]);

  return live;
}
